import { Option } from 'vnetwork-ui/dist/components/FormItem'
import { AsyncFilterSelectPage } from '@/components/ui/filters'

// Import existing services
import {
    getEmployeesOptions,
    getEmployeeContains,
    getAllDepartments,
    getPositionsByDepartment,
} from '@/modules/Employee/services'
import {
    getCustomerOptions,
    getCustomerContains,
} from '@/modules/Customer/services'
import { getCompaniesOptions } from '@/modules/Company/services'
import { getTicketOptions } from '@/modules/Ticket/services'
import { getProductsGroupOptions } from '@/modules/ProductGroup/services'
import datacentral from '@/utils/axios/datacentral'
import qs from 'query-string'
import { Position } from '@/modules/Employee/types/response'

const DEFAULT_PAGE_SIZE = 20

/**
 * Entity configuration for AsyncFilterSelect
 */
export interface EntityConfig {
    /** Fetch options (legacy - returns array) */
    fetchOptions: (
        query: string,
        params?: Record<string, any>
    ) => Promise<Option[]>
    /** Fetch options with pagination support */
    fetchOptionsWithPage: (
        query: string,
        page: number,
        params?: Record<string, any>
    ) => Promise<AsyncFilterSelectPage>
    /** Fetch options for selected values */
    fetchByIds: (ids: any[], params?: Record<string, any>) => Promise<Option[]>
}

// Helper: wrap fetchOptions for pagination (no real pagination, single page)
const wrapForPagination = (
    fetchFn: (query: string, params?: Record<string, any>) => Promise<Option[]>
): EntityConfig['fetchOptionsWithPage'] => {
    return async (query, page, params) => {
        if (page > 1) return { options: [], hasMore: false }
        const options = await fetchFn(query, params)
        return { options, hasMore: false }
    }
}

// ==================== Employee ====================
const employeeConfig: EntityConfig = {
    fetchOptions: async (query, params) => {
        return getEmployeesOptions(
            query,
            params?.departmentIds,
            params?.activeOnly ? 'active' : undefined
        )
    },
    fetchOptionsWithPage: async (query, page, params) => {
        // Pagination-enabled fetch (services don't support pagination yet)
        const response = await datacentral.get(
            `/employees/options?${qs.stringify({
                q: query,
                page,
                page_size: DEFAULT_PAGE_SIZE,
                department_ids: params?.departmentIds,
                employee_status: params?.activeOnly ? 'active' : undefined,
            })}`
        )
        if (!response?.data?.results) return { options: [], hasMore: false }
        const options = response.data.results.map((e: any) => ({
            label: e.employee_name,
            value: e.employee_id,
        }))
        return {
            options,
            hasMore: options.length >= DEFAULT_PAGE_SIZE,
            nextPage: page + 1,
        }
    },
    fetchByIds: async (ids) => {
        if (!ids?.length) return []
        return getEmployeeContains({ employee_ids: ids })
    },
}

// ==================== Customer ====================
const customerConfig: EntityConfig = {
    fetchOptions: async (query, _params) => {
        return getCustomerOptions({ q: query, page: 1, page_size: 50 })
    },
    fetchOptionsWithPage: async (query, page) => {
        const options = await getCustomerOptions({
            q: query,
            page,
            page_size: DEFAULT_PAGE_SIZE,
        })
        return {
            options,
            hasMore: options.length >= DEFAULT_PAGE_SIZE,
            nextPage: page + 1,
        }
    },
    fetchByIds: async (ids) => {
        if (!ids?.length) return []
        return getCustomerContains({ customer_ids: ids })
    },
}

// ==================== Department ====================
const departmentConfig: EntityConfig = {
    fetchOptions: async (_query, _params) => {
        const response = await getAllDepartments()
        if (!response?.results) return []
        return response.results.map((d) => ({
            label: d.department_name,
            value: d.department_id,
        }))
    },
    fetchOptionsWithPage: wrapForPagination(async (_query, _params) => {
        const response = await getAllDepartments()
        if (!response?.results) return []
        return response.results.map((d) => ({
            label: d.department_name,
            value: d.department_id,
        }))
    }),
    fetchByIds: async (ids) => {
        const response = await getAllDepartments()
        if (!response?.results) return []
        return response.results
            .filter((d) => ids.includes(d.department_id))
            .map((d) => ({
                label: d.department_name,
                value: d.department_id,
            }))
    },
}

// ==================== Position ====================
const positionConfig: EntityConfig = {
    fetchOptions: async (_query, params) => {
        if (!params?.departmentId) return []
        const response = await getPositionsByDepartment(params.departmentId)
        if (!response?.results) return []
        return response.results.map((p: Position) => ({
            label: p.position_name,
            value: p.position_id,
        }))
    },
    fetchOptionsWithPage: wrapForPagination(async (_query, params) => {
        if (!params?.departmentId) return []
        const response = await getPositionsByDepartment(params.departmentId)
        if (!response?.results) return []
        return response.results.map((p: Position) => ({
            label: p.position_name,
            value: p.position_id,
        }))
    }),
    fetchByIds: async (ids, params) => {
        if (!params?.departmentId) return []
        const response = await getPositionsByDepartment(params.departmentId)
        if (!response?.results) return []
        return response.results
            .filter((p: Position) => ids.includes(p.position_id))
            .map((p: Position) => ({
                label: p.position_name,
                value: p.position_id,
            }))
    },
}

// ==================== Ticket ====================
const ticketConfig: EntityConfig = {
    fetchOptions: async (query, _params) => getTicketOptions(query),
    fetchOptionsWithPage: wrapForPagination(async (query, _params) =>
        getTicketOptions(query)
    ),
    fetchByIds: async (ids) => {
        if (!ids?.length) return []
        const allOptions = await getTicketOptions('')
        return allOptions.filter((opt) => ids.includes(opt.value))
    },
}

// ==================== Company ====================
const companyConfig: EntityConfig = {
    fetchOptions: async (query, _params) => getCompaniesOptions(query),
    fetchOptionsWithPage: async (query, page) => {
        // Company service doesn't support pagination, fetch all and slice
        if (page > 1) return { options: [], hasMore: false }
        const options = await getCompaniesOptions(query)
        return { options, hasMore: false }
    },
    fetchByIds: async (ids) => {
        if (!ids?.length) return []
        const allOptions = await getCompaniesOptions('')
        return allOptions.filter((opt) => ids.includes(opt.value))
    },
}

// ==================== Product Group ====================
const productGroupConfig: EntityConfig = {
    fetchOptions: async (_query, _params) => getProductsGroupOptions(),
    fetchOptionsWithPage: wrapForPagination(async (_query, _params) =>
        getProductsGroupOptions()
    ),
    fetchByIds: async (names) => {
        if (!names?.length) return []
        const allOptions = await getProductsGroupOptions()
        return allOptions.filter((opt) => names.includes(opt.value))
    },
}

/**
 * Entity type for SimpleAsyncSelect
 */
export type EntityType =
    | 'employee'
    | 'customer'
    | 'department'
    | 'position'
    | 'ticket'
    | 'company'
    | 'product-group'

/**
 * Get entity configuration by type
 */
export const getEntityConfig = (entity: EntityType): EntityConfig => {
    const configs: Record<EntityType, EntityConfig> = {
        employee: employeeConfig,
        customer: customerConfig,
        department: departmentConfig,
        position: positionConfig,
        ticket: ticketConfig,
        company: companyConfig,
        'product-group': productGroupConfig,
    }
    return configs[entity]
}
